import { ChatBubbleLeftRightIcon } from '@heroicons/react/24/outline'
import { ReactElement } from 'react'
import { OpsTrendGraph, type TrendSeries } from 'app/components/ai-agent-voice-ops/OpsTrendGraph'
import { SectionTitle } from 'app/components/ai-agent-voice-ops/SectionTitle'
import { StatsRow } from 'app/components/ai-agent-voice-ops/StatsRow'
import SimpleTable from 'app/components/table/SimpleTable'
import { TableEmptyState } from 'app/components/ui/table-empty-state'
import { AiQualitySection } from './AiQualitySection'
import { AiReplyHealthBanner } from './AiReplyHealthBanner'
import { DataSourceBadge } from './DataSourceBadge'
import { FailedRepliesSection } from './FailedRepliesSection'
import { FailuresSection } from './FailuresSection'
import { PreviewDataNotice } from './PreviewDataNotice'
import { ReplyWaitsSection } from './ReplyWaitsSection'
import { SilentAgentsSection } from './SilentAgentsSection'
import { EMPTY_DAILY_TOTALS, type SmsDailyField } from './smsAgentOpsMetrics'
import { SENDER_LABEL } from './smsSenders'
import { TakeoversSection } from './TakeoversSection'
import type { UseSmsAgentOpsReturn } from './useSmsAgentOps'
import { WorkflowsSection } from './WorkflowsSection'
import { WorseningSection } from './WorseningSection'

const TEXT_SERIES: TrendSeries<SmsDailyField>[] = [
  { key: `Patient`, color: `gray`, value: (totals) => totals.inbound },
  { key: SENDER_LABEL.agent, color: `purple`, value: (totals) => totals.agentTexts },
  { key: SENDER_LABEL.workflow, color: `blue`, value: (totals) => totals.workflowTexts },
  { key: SENDER_LABEL.staff, color: `green`, value: (totals) => totals.staffTexts },
  { key: `Other`, color: `sky`, value: (totals) => totals.otherTexts },
]

/**
 * The SMS Ops tab, top to bottom (plan D6, D33, D35): the AI reply health banner while
 * patients are waiting → cards → Waiting on the AI → Missed AI Replies → Texts → Location Breakdown →
 * takeovers (chart, by location, by staff, log) → Getting Worse → Silent Agents →
 * AI quality → failures and opt-outs → Workflows. Tables grow with their rows; the
 * page does the scrolling.
 */
export function SmsAgentOpsComponent(props: UseSmsAgentOpsReturn): ReactElement {
  const {
    fromDate,
    toDate,
    isLoading,
    summary,
    volumeStats,
    agentStats,
    healthStats,
    daily,
    locationRows,
    locationColumns,
  } = props

  return (
    <>
      <AiReplyHealthBanner {...props} />
      <PreviewDataNotice />

      <div className="space-y-3 md:space-y-4">
        <StatsRow stats={volumeStats} isLoading={isLoading} skeletonCount={5} />
        <StatsRow stats={agentStats} isLoading={isLoading} skeletonCount={5} />
        <StatsRow stats={healthStats} isLoading={isLoading} skeletonCount={5} />
      </div>

      <div className="mt-8">
        <OpsTrendGraph
          title="Texts"
          adornment={<DataSourceBadge source="preview" />}
          badge={`${(summary.inbound + summary.outbound).toLocaleString(`en-US`)} Texts`}
          series={TEXT_SERIES}
          daily={daily}
          emptyTotals={EMPTY_DAILY_TOTALS}
          fromDate={fromDate}
          toDate={toDate}
          isLoading={isLoading}
        />
      </div>

      <div className="mb-8">
        <div className="mb-3">
          <SectionTitle
            title="Location Breakdown"
            badge={`${locationRows?.length ?? 0} Locations`}
            adornment={<DataSourceBadge source="preview" />}
          />
        </div>
        <SimpleTable
          activeSubpage="sms-agent-ops-locations"
          data={locationRows}
          columns={locationColumns}
          initialSort={[{ id: `outbound`, desc: true }]}
          enableDeepLinking={false}
          emptyState={
            <TableEmptyState
              icon={<ChatBubbleLeftRightIcon className="h-6 w-6" />}
              title="No texting locations"
              description="Locations that sent or received texts in the date range show up here."
            />
          }
        />
      </div>

      <ReplyWaitsSection {...props} />
      <FailedRepliesSection {...props} />
      <TakeoversSection {...props} />
      <WorseningSection {...props} />
      <SilentAgentsSection {...props} />
      <AiQualitySection {...props} />
      <FailuresSection {...props} />
      <WorkflowsSection {...props} />
    </>
  )
}
